/*
 * HomeDashboard — Prepare Up's learning workspace.
 *
 * Answers, top to bottom: what am I working towards, what needs me today,
 * where did I leave off, what do I know, and what should I do next.
 *
 * Every number on this page comes from data the app already has — the engine
 * memory tree (/engine/memory/tree), the hub tree (/hub/tree) and the locally
 * persisted sessions (sessionStore). Nothing is invented: a section with no
 * signal yet says so and offers the next step instead.
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import '../../styles/home-dashboard.css';   // still hosts the MemoryField styles used by MemoryPage
import '../../styles/prepare-up-home.css';
import APIService from '../../services/APIService';
import * as sessionStore from './sessionStore';
import { buildPaths, getHubTreeCached, indexTree, leavesOf, pathMastery, sessionWithinPath } from './paths';
import { IconArrowRight, IconBars, IconBolt, IconBook, IconChat, IconCheck, IconChevDown, IconMoon, IconSearch, IconSun } from './icons';
import { MODES as MODE_REGISTRY } from './modes';

const GOAL_KEY = 'pu_goal_v1';
const pct = (value) => `${Math.round((Number(value) || 0) * 100)}%`;
const number = (value) => new Intl.NumberFormat().format(Number(value) || 0);

/* What each state of a core skill asks of the learner — short, action-first. */
const ATTENTION = {
  weak: { action: 'Practice', note: 'Practice questions', flag: 'Weak area' },
  fading: { action: 'Review', note: 'Review key concepts', flag: 'Fading' },
  learning: { action: 'Continue', note: 'Continue learning' },
  new: { action: 'Start', note: 'Not started · core concepts' },
};
const RANK = { weak: 0, fading: 1, learning: 2 };

// A session's mode → the kind of work it is.
const KIND = {
  theory: 'Learning', flashcards: 'Learning',
  mcq: 'Practice', dsa: 'Practice', blitz: 'Practice',
  interviewer: 'Interview', rapidfire: 'Interview',
};

const relTime = (iso) => {
  const t = Date.parse(iso || '');
  if (!t) return '—';
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export const loadGoal = () => { try { return localStorage.getItem(GOAL_KEY) || ''; } catch { return ''; } };
export const saveGoal = (id) => { try { localStorage.setItem(GOAL_KEY, id); } catch { /* private mode */ } };

/** The learner's goal track: their saved pick, else the track the current
 *  session sits in, else the track they've practised most. Shared with Practice. */
export const resolveGoal = (goalOptions, goalId, session, memTree) => {
  if (!goalOptions.length) return null;
  return goalOptions.find((p) => p.id === goalId)
    || goalOptions.find((p) => sessionWithinPath(session, p))
    || [...goalOptions].sort((a, b) => (pathMastery(b, memTree || []) ?? -1) - (pathMastery(a, memTree || []) ?? -1))[0];
};

/** Search entries — every hub node (with its breadcrumb) and every track. */
export const buildSearchEntries = (hub, tracks) => {
  const out = [];
  const walk = (nodes, trail, depth) => (nodes || []).forEach((n) => {
    out.push({ kind: 'node', id: n.id, name: n.name || n.id, trail: trail.join(' › '), depth });
    walk(n.children, [...trail, n.name], depth + 1);
  });
  walk(hub, [], 0);
  (tracks || []).forEach((p) => out.push({ kind: 'track', id: p.id, name: p.title, trail: '', depth: -1 }));
  return out;
};

/* ── Search: every hub node and track, matched by name ───────────────────── */
export const HomeSearch = ({ entries, onPick }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listId = useId();

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return entries
      .filter((e) => e.name.toLowerCase().includes(q))
      .sort((a, b) => (a.name.toLowerCase().startsWith(q) ? 0 : 1) - (b.name.toLowerCase().startsWith(q) ? 0 : 1)
        || a.depth - b.depth)
      .slice(0, 8);
  }, [entries, query]);

  // ⌘K / Ctrl+K focuses search from anywhere on Home.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); inputRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pick = (entry) => { setOpen(false); setQuery(''); onPick(entry); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); pick(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };
  const showList = open && query.trim().length >= 2;

  return (
    <div className="pu-search" role="search">
      <IconSearch size={16} />
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder="Search skills, topics or anything…"
        aria-label="Search skills and topics"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && results[active] ? `${listId}-${active}` : undefined}
        onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
      />
      <kbd aria-hidden="true">⌘K</kbd>
      {showList && (
        <ul className="pu-search-list" id={listId} role="listbox" aria-label="Search results">
          {results.length ? results.map((r, i) => (
            <li key={`${r.kind}-${r.id}`} id={`${listId}-${i}`} role="option" aria-selected={i === active}
              className={i === active ? 'active' : ''}
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              onMouseEnter={() => setActive(i)}>
              <b>{r.name}</b>
              <span>{r.kind === 'track' ? 'Track' : r.trail || 'Topic'}</span>
            </li>
          )) : <li className="pu-search-empty" role="option" aria-selected="false" aria-disabled="true">No topic matches “{query.trim()}”.</li>}
        </ul>
      )}
    </div>
  );
};

/* ── The learning journey: goal → learn → practice → build → prove ──────── */
const JOURNEY = [
  { id: 'learn', step: 'Learn', note: 'Core concepts', Icon: IconBook },
  { id: 'practice', step: 'Practice', note: 'Questions', Icon: IconBolt },
  { id: 'build', step: 'Build', note: 'Real problems', Icon: IconBars },
  { id: 'prove', step: 'Prove', note: 'In interviews', Icon: IconChat },
];

const JourneyMap = ({ goalTitle, actions }) => (
  <figure className="pu-journey" aria-label="How Prepare Up gets you there">
    <svg className="pu-journey-lines" viewBox="0 0 600 240" preserveAspectRatio="none" aria-hidden="true">
      <path d="M168 120 H191 V60 H214" />
      <path d="M191 120 V180 H214" />
      <path d="M380 60 H403 V84 H426" />
      <path d="M380 180 H403 V156 H426" />
      <path d="M403 84 V156" />
      <circle cx="191" cy="120" r="3" />
      <circle cx="403" cy="120" r="3" />
    </svg>
    <ol>
      <li className="pu-journey-goal">
        <span>Your goal</span>
        <b>{goalTitle || 'Choose a goal'}</b>
      </li>
      {JOURNEY.map((j) => (
        <li key={j.id} className={`pu-journey-step s-${j.id}`}>
          <button type="button" onClick={actions[j.id]}>
            <j.Icon size={15} />
            <span><b>{j.step}</b><small>{j.note}</small></span>
          </button>
        </li>
      ))}
    </ol>
  </figure>
);

/* ── Goal picker: a compact select-only combobox anchored under the goal ──
   (a native <select> opens the OS-sized menu, which swamps the page). */
const GoalPicker = ({ labelId, value, groups, onChange }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const current = flat.find((o) => o.id === value);

  const openList = () => { setActive(Math.max(0, flat.findIndex((o) => o.id === value))); setOpen(true); };
  const choose = (o) => { onChange(o.id); setOpen(false); btnRef.current?.focus(); };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  useEffect(() => {
    if (open) listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const onKeyDown = (e) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); openList(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(flat.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (flat[active]) choose(flat[active]); }
    else if (e.key === 'Escape' || e.key === 'Tab') { setOpen(false); }
  };

  let index = -1;
  return (
    <div className="pu-goal-pick" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        role="combobox"
        className="pu-goal-btn"
        aria-labelledby={labelId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && flat[active] ? `${listId}-${active}` : undefined}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
      >
        <span>{current?.title || 'Choose a goal'}</span>
        <IconChevDown size={18} />
      </button>
      {open && (
        <div className="pu-goal-menu" id={listId} role="listbox" aria-labelledby={labelId} ref={listRef}>
          {groups.map((g) => (
            <div role="group" aria-label={g.label} key={g.label}>
              <div className="pu-goal-group" aria-hidden="true">{g.label}</div>
              {g.items.map((o) => {
                index += 1;
                const i = index;
                const selected = o.id === value;
                return (
                  <div
                    key={o.id}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={selected}
                    data-active={i === active}
                    className={`pu-goal-opt${i === active ? ' active' : ''}${selected ? ' selected' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); choose(o); }}
                    onMouseEnter={() => setActive(i)}
                  >
                    <span>{o.title}</span>
                    {selected && <IconCheck size={15} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* Light / dark switch: sun · pill · moon. A real switch for assistive tech;
   the knob position (not just colour) shows the state. */
export const ThemeToggle = ({ theme, onToggle }) => {
  const dark = theme === 'dark';
  return (
    <button type="button" role="switch" aria-checked={dark} aria-label="Dark mode"
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={`pu-theme${dark ? ' is-dark' : ''}`} onClick={onToggle}>
      <IconSun size={16} />
      <span className="pu-theme-track" aria-hidden="true"><span className="pu-theme-knob" /></span>
      <IconMoon size={15} />
    </button>
  );
};

export const Bar = ({ value, label }) => (
  <span className="pu-bar" role="img" aria-label={label}>
    <i style={{ width: `${Math.max(0, Math.min(100, Math.round((value || 0) * 100)))}%` }} />
  </span>
);

export const Loading = ({ rows = 3 }) => (
  <div className="pu-loading" aria-label="Loading">{Array.from({ length: rows }).map((_, i) => <i key={i} />)}</div>
);

export default function HomeDashboard({
  profile,
  onEditProfile,
  session,
  onResume,
  onOpenSettings,
  onOpenPath,
  onViewPlan,
  onOpenMemory,
  onOpenHub,
  onSample,
  onOpenDsa,
  onOpenInterviews,
  onOpenHubNode,
  theme = 'light',
  onToggleTheme,
}) {
  const [memory, setMemory] = useState(null);
  const [memoryState, setMemoryState] = useState('loading');
  const [hub, setHub] = useState(null);
  const [goalId, setGoalId] = useState(loadGoal);

  useEffect(() => {
    let live = true;
    APIService.getMemoryTree()
      .then((data) => { if (live) { setMemory(data || {}); setMemoryState('ready'); } })
      .catch(() => { if (live) setMemoryState('unavailable'); });
    getHubTreeCached()
      .then((data) => { if (live) setHub(data.tree || []); })
      .catch(() => { if (live) setHub([]); });
    return () => { live = false; };
  }, []);

  const memTree = memory?.tree;
  const memIndex = useMemo(() => indexTree(memTree || []), [memTree]);
  const paths = useMemo(() => (hub ? buildPaths(hub) : null), [hub]);
  const trend = memory?.trend || [];
  const thisWeek = trend.find((week) => week.weeks_ago === 0);

  /* Goal — a track (role bundle or area) from the hub. The learner's pick is
     remembered; otherwise the track the current session sits in. */
  const goalOptions = useMemo(() => (paths ? [...paths.roles, ...paths.areas] : []), [paths]);
  const goal = useMemo(() => resolveGoal(goalOptions, goalId, session, memTree), [goalOptions, goalId, session, memTree]);
  const chooseGoal = (id) => { setGoalId(id); saveGoal(id); };

  // Core skills = the subtopics directly under the goal's hub roots.
  const coreSkills = useMemo(() => (goal && paths
    ? goal.rootIds.flatMap((id) => paths.byId[id]?.children || [])
    : []), [goal, paths]);
  const strongCount = coreSkills.filter((c) => memIndex[c.id]?.status === 'strong').length;
  const goalShare = coreSkills.length ? strongCount / coreSkills.length : 0;

  /* What needs attention — the goal's own core skills: weak, fading and
     in-progress first (weakest first), then ones not started yet. */
  const attention = useMemo(() => {
    const practised = coreSkills
      .filter((c) => memIndex[c.id]?.attempts > 0 && RANK[memIndex[c.id].status] != null)
      .sort((a, b) => (RANK[memIndex[a.id].status] - RANK[memIndex[b.id].status])
        || ((memIndex[a.id].mastery || 0) - (memIndex[b.id].mastery || 0)))
      .map((c) => ({ node: c, state: memIndex[c.id].status, mastery: memIndex[c.id].mastery }));
    const fresh = coreSkills
      .filter((c) => !(memIndex[c.id]?.attempts > 0))
      .map((c) => ({ node: c, state: 'new', mastery: null }));
    return [...practised, ...fresh].slice(0, 3);
  }, [coreSkills, memIndex]);

  /* Skill memory — the most-practised subtopics and how much each holds. */
  const skillMemory = useMemo(() => {
    const subs = (memTree || []).flatMap((root) => root.children || []);
    const pool = subs.filter((n) => n.attempts > 0);
    const list = pool.length ? pool : (memTree || []).filter((n) => n.attempts > 0);
    return [...list].sort((a, b) => (b.attempts || 0) - (a.attempts || 0)).slice(0, 5);
  }, [memTree]);

  /* Continue — the persisted session history, newest first. */
  const recent = useMemo(() => {
    const hist = sessionStore.history() || [];
    const list = session ? [session, ...hist.filter((h) => h && h.id !== session.id)] : hist;
    return list.filter((s) => s && (s.subjects || []).length).slice(0, 3);
  }, [session]);
  const heldFor = useCallback((subjects) => {
    let att = 0; let acc = 0;
    (subjects || []).forEach((s) => {
      const n = memIndex[s.id];
      if (n && n.attempts > 0) { att += n.attempts; acc += (n.mastery || 0) * n.attempts; }
    });
    return att ? acc / att : null;
  }, [memIndex]);

  /* Search entries — every hub node (with its breadcrumb) and every track. */
  const searchEntries = useMemo(() => buildSearchEntries(hub, goalOptions), [hub, goalOptions]);

  // Practising a skill seeds Session Settings' selection (it takes LEAF ids),
  // then opens it.
  const practiceSkill = (node) => {
    const draft = sessionStore.loadDraft() || {};
    sessionStore.saveDraft({ ...draft, sel: leavesOf(node).map((n) => n.id) });
    onOpenSettings();
  };
  const runAttention = (item) => (item.state === 'weak' || item.state === 'learning'
    ? practiceSkill(item.node)
    : onOpenHubNode?.(item.node.id));

  /* Recommendations — each derived from a real signal, shown only if present. */
  const recommendations = useMemo(() => {
    const recs = [];
    const weak = attention.find((a) => a.state === 'weak' || a.state === 'fading');
    if (weak) {
      recs.push({ id: 'practice', kind: 'Practice', title: weak.node.name,
        meta: `Based on your weak areas · ${pct(weak.mastery)} held`, run: () => practiceSkill(weak.node) });
    }
    if (goal && coreSkills.length) {
      const fresh = coreSkills.find((c) => !(memIndex[c.id]?.attempts > 0));
      const lowest = [...coreSkills].filter((c) => memIndex[c.id]?.attempts > 0)
        .sort((a, b) => (memIndex[a.id].mastery || 0) - (memIndex[b.id].mastery || 0))[0];
      const pick = fresh || lowest;
      if (pick) {
        recs.push({ id: 'learn', kind: 'Learning', title: pick.name,
          meta: fresh ? 'Not started yet · matches your goal' : `Lowest in your goal · ${pct(memIndex[pick.id].mastery)} held`,
          run: () => onOpenHubNode?.(pick.id) });
      }
    }
    if (goal) {
      recs.push({ id: 'interview', kind: 'Interview', title: `${goal.title} Mock Interview`,
        meta: 'Practice under interview conditions', run: onOpenInterviews });
    }
    return recs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attention, goal, coreSkills, memIndex, onOpenHubNode, onOpenInterviews]);

  const displayName = profile?.display_name || profile?.name || '';
  const today = new Date().toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const loadingMemory = memoryState === 'loading';

  const primaryCta = session
    ? { label: session.status === 'planned' ? 'Review your plan' : 'Continue learning', run: onResume }
    : attention[0]
      ? { label: 'Continue learning', run: () => runAttention(attention[0]) }
      : { label: 'Plan your first session', run: onOpenSettings };

  return (
    <div className="pu-home">
      <div className="pu-page">
        <header className="pu-topbar">
          <HomeSearch
            entries={searchEntries}
            onPick={(e) => (e.kind === 'track' ? onOpenPath(e.id) : onOpenHubNode?.(e.id))}
          />
          <div className="pu-topbar-end">
          {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
          <button type="button" className="pu-avatar" onClick={onEditProfile}
            aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
            {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
          </button>
          </div>
        </header>

        {/* 1 · Hero */}
        <section className="pu-hero" aria-labelledby="pu-hero-title">
          <div className="pu-hero-copy">
            <p className="pu-kicker">Your learning workspace</p>
            <h1 id="pu-hero-title">Don’t guess what to study.</h1>
            <p className="pu-lede">Know what matters for your goal. Learn it. Practice it. Prove it.</p>
            <div className="pu-hero-actions">
              <button type="button" className="pu-btn pu-btn-primary" onClick={primaryCta.run}>
                {primaryCta.label} <IconArrowRight size={16} />
              </button>
              <button type="button" className="pu-btn pu-btn-link" onClick={onOpenHub}>
                Explore all topics <IconArrowRight size={15} />
              </button>
            </div>
          </div>
          <JourneyMap
            goalTitle={goal?.title}
            actions={{ learn: onOpenHub, practice: onOpenSettings, build: onOpenDsa, prove: onOpenInterviews }}
          />
        </section>

        {/* 2 · Today */}
        <section className="pu-today pu-panel" aria-labelledby="pu-today-title">
          <div className="pu-goal">
            <div className="pu-row-head">
              <h2 id="pu-today-title">Today</h2>
              <span className="pu-meta">{today}</span>
            </div>
            <span className="pu-label" id="pu-goal-label">Your current goal</span>
            {goalOptions.length ? (
              <GoalPicker
                labelId="pu-goal-label"
                value={goal?.id}
                groups={[
                  { label: 'Roles', items: paths.roles },
                  { label: 'Topics', items: paths.areas },
                ].filter((g) => g.items.length)}
                onChange={chooseGoal}
              />
            ) : <div className="pu-goal-pick is-empty">{hub == null ? 'Loading tracks…' : 'No tracks yet'}</div>}
            {loadingMemory ? <Loading rows={2} /> : (
              <>
                <p className="pu-goal-count">
                  {coreSkills.length
                    ? <>{strongCount} of {coreSkills.length} core skills strong</>
                    : 'Pick a goal to see its core skills'}
                </p>
                {coreSkills.length > 0 && (
                  <div className="pu-goal-bar">
                    <Bar value={goalShare} label={`${Math.round(goalShare * 100)}% of core skills strong`} />
                    <b>{Math.round(goalShare * 100)}%</b>
                  </div>
                )}
                <p className="pu-meta pu-week">
                  {thisWeek?.attempts
                    ? `This week: ${number(thisWeek.attempts)} answers${thisWeek.accuracy != null ? ` · ${pct(thisWeek.accuracy)} accuracy` : ''}`
                    : 'No answers logged this week yet.'}
                </p>
              </>
            )}
          </div>

          <div className="pu-attention">
            <div className="pu-row-head">
              <h3>What needs your attention</h3>
              <button type="button" className="pu-link" onClick={onOpenMemory}>View all <IconArrowRight size={13} /></button>
            </div>
            {loadingMemory ? <Loading /> : memoryState === 'unavailable' ? (
              <p className="pu-empty">We couldn’t load your progress right now. You can still plan a session from the Knowledge Hub.</p>
            ) : attention.length ? (
              <ol className="pu-attn-list">
                {attention.map((item, i) => {
                  const a = ATTENTION[item.state] || ATTENTION.learning;
                  return (
                    <li key={item.node.id}>
                      <span className="pu-num" aria-hidden="true">{i + 1}</span>
                      <div className="pu-attn-body">
                        <b title={item.node.name}>{item.node.name}</b>
                        <span>
                          {a.note}
                          {item.mastery != null && ` · ${pct(item.mastery)} held`}
                          {a.flag && <em className={`pu-flag${item.state === 'fading' ? ' soft' : ''}`}> · {a.flag}</em>}
                        </span>
                      </div>
                      <button type="button" className={`pu-btn pu-btn-sm${i === 0 ? ' pu-btn-primary' : ' pu-btn-quiet'}`}
                        onClick={() => runAttention(item)} aria-label={`${a.action} ${item.node.name}`}>
                        {a.action} <IconArrowRight size={13} />
                      </button>
                    </li>
                  );
                })}
              </ol>
            ) : coreSkills.length ? (
              <p className="pu-empty">Every core skill in {goal.title} is holding strong. Pick a new goal or explore a topic.</p>
            ) : (
              <div className="pu-empty">
                <p>Pick a goal and its core skills appear here.</p>
                <div className="pu-empty-actions">
                  <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={onSample}>Try a sample question</button>
                  <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={onOpenSettings}>Plan a session</button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* 3 · Continue + Skill memory */}
        <div className="pu-split">
          <section className="pu-continue pu-panel" aria-labelledby="pu-continue-title">
            <div className="pu-row-head">
              <h2 id="pu-continue-title">Continue where you left off</h2>
              <button type="button" className="pu-link" onClick={onOpenSettings}>Plan a session <IconArrowRight size={13} /></button>
            </div>
            {recent.length ? (
              <table className="pu-table">
                <thead>
                  <tr><th scope="col">Type</th><th scope="col">Topic</th><th scope="col">Progress</th><th scope="col">Last activity</th><th scope="col"><span className="pu-sr">Action</span></th></tr>
                </thead>
                <tbody>
                  {recent.map((s) => {
                    const isCurrent = session && s.id === session.id;
                    const held = heldFor(s.subjects);
                    const names = (s.subjects || []).map((x) => x.name).filter(Boolean);
                    const modeLabel = MODE_REGISTRY[s.mode]?.label || 'Session';
                    return (
                      <tr key={s.id}>
                        <td data-label="Type"><span className="pu-type">{KIND[s.mode] || 'Practice'}</span></td>
                        <td data-label="Topic">
                          <b>{names.slice(0, 2).join(' · ')}{names.length > 2 ? ` +${names.length - 2}` : ''}</b>
                          <small>{modeLabel}{s.status === 'planned' ? ' · Planned' : isCurrent ? ' · Current session' : ''}</small>
                        </td>
                        <td data-label="Progress">
                          {held == null ? <span className="pu-meta">Not started</span> : (
                            <span className="pu-progress"><Bar value={held} label={`${pct(held)} held`} /><b>{pct(held)}</b></span>
                          )}
                        </td>
                        <td data-label="Last activity"><span className="pu-meta">{relTime(s.updatedAt)}</span></td>
                        <td className="pu-td-action">
                          {isCurrent ? (
                            <span className="pu-row-actions">
                              <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={onResume}>
                                {s.status === 'planned' ? 'Review' : 'Resume'} <IconArrowRight size={13} />
                              </button>
                              {s.plan && s.status !== 'planned' && (
                                <button type="button" className="pu-link" onClick={onViewPlan}>Plan</button>
                              )}
                            </span>
                          ) : (
                            <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet"
                              onClick={() => onOpenHubNode?.(s.subjects[0].id)} aria-label={`Review ${names[0] || 'topic'} in the Knowledge Hub`}>
                              Review <IconArrowRight size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="pu-empty">
                <p><b>Nothing to continue yet.</b> Sessions you plan or start will be listed here.</p>
                <button type="button" className="pu-link" onClick={onOpenSettings}>Plan a session <IconArrowRight size={13} /></button>
              </div>
            )}
          </section>

          <section className="pu-memory pu-panel" aria-labelledby="pu-memory-title">
            <div className="pu-row-head">
              <h2 id="pu-memory-title">Your Skill Memory</h2>
              <button type="button" className="pu-link" onClick={onOpenMemory}>View graph <IconArrowRight size={13} /></button>
            </div>
            {loadingMemory ? <Loading rows={5} /> : skillMemory.length ? (
              <ul className="pu-skill-list">
                {skillMemory.map((n) => (
                  <li key={n.id}>
                    <span className="pu-skill-name">{n.name}</span>
                    <Bar value={n.mastery} label={`${n.name}: ${pct(n.mastery)} held`} />
                    <b>{pct(n.mastery)}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pu-empty">Practise a topic and how well you hold it shows up here.</p>
            )}
          </section>
        </div>

        {/* 4 · Explore by topic */}
        <section className="pu-explore" aria-labelledby="pu-explore-title">
          <div className="pu-row-head">
            <h2 id="pu-explore-title">Explore by topic</h2>
            <button type="button" className="pu-link" onClick={onOpenHub}>See all topics <IconArrowRight size={13} /></button>
          </div>
          {!paths ? <Loading rows={2} /> : paths.areas.length ? (
            <ol className="pu-topic-list">
              {paths.areas.slice(0, 6).map((p, i) => (
                <li key={p.id}>
                  <button type="button" onClick={() => onOpenPath(p.id)}>
                    <span className="pu-topic-num">{String(i + 1).padStart(2, '0')}</span>
                    <b>{p.title}</b>
                    <small>{p.blurb}</small>
                    <span className="pu-topic-go">{number(p.count)} items <IconArrowRight size={12} /></span>
                  </button>
                </li>
              ))}
            </ol>
          ) : <p className="pu-empty">Add a topic to the Knowledge Hub and it appears here.</p>}
        </section>

        {/* 5 · Recommended */}
        {recommendations.length > 0 && (
          <section className="pu-recs" aria-labelledby="pu-recs-title">
            <div className="pu-row-head">
              <h2 id="pu-recs-title">Recommended for you</h2>
              <span className="pu-meta">Based on your goal and weak areas</span>
            </div>
            <ul className="pu-rec-list">
              {recommendations.map((r) => (
                <li key={r.id}>
                  <button type="button" className={`pu-rec k-${r.id}`} onClick={r.run}>
                    <span className="pu-rec-kind">{r.kind}</span>
                    <b>{r.title}</b>
                    <small>{r.meta}</small>
                    <IconArrowRight size={16} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
